import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MessageCircle } from "lucide-react";
import { IconButton } from "@cntrl/ui";
import { useInboxBadge } from "../../app/hooks/useInbox.js";
import { useMessagePanel } from "../../app/messagePanel.js";
import type { ShowLinkResolver } from "../../app/showLink.js";
import { InboxScreen } from "./InboxScreen.js";
import styles from "./InboxButton.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

export interface InboxButtonProps {
  /** Where this shell's inbox lives as a PAGE — mobile navigates here, and a deep link lands here. */
  route: string;
  onNavigate?: (route: string) => void;
  /**
   * Where a show chat's SHOW lives, from THIS shell (ART-159, C-Mobile) — the Day of Show link. The same
   * shape as `route` one level down: the shell knows its own addresses and the inbox does not, because this
   * button mounts in the topbar, outside the artist shell's own `BandProvider`.
   */
  showHref?: ShowLinkResolver;
}

/**
 * THE ENVELOPE — unread badge, and either the inbox OVER the page you are on or a jump to the full page.
 *
 * **Opening messages must not cost you the page you were reading (PO, 2026-09-11).** It used to navigate to
 * `/messages`, so pressing the envelope unmounted whatever you had open — a half-filled show editor, a
 * scrolled release — and closing it landed you on the DASHBOARD rather than back where you started, because
 * the routed screen's `onClose` went to `/`. Two separate ways to lose your place from one button. The
 * handoff has always described this surface as an overlay ("Envelope → MessagesOverlay, portal to
 * document.body"); it was the routing that disagreed with the design, not the design that was unclear.
 *
 * **The ROUTE survives, and that is not a hedge.** A notification tap deep-links to `/messages/{id}`, a
 * restored tab reopens it, and mobile uses it as a real page from its tab bar — so the page keeps its
 * `requireInbox` guard and its place in the router. What changed is only that the BUTTON no longer uses it.
 * Deleting the route would have moved the deep-link problem somewhere else rather than solving it.
 *
 * **This is `NotificationBell`'s shape, deliberately.** That control answered the same question first — a
 * panel in place on desktop, a page on mobile — so the envelope matching it means the two neighbours in the
 * topbar behave the same way, and the badge treatment they share stops being three copies. The two shells
 * each carried their own identical badge markup and CSS; both now read from here.
 *
 * **The open thread is LOCAL STATE while the overlay is up, and that is the whole fix.** Putting it in the
 * URL is what made the inbox a destination. The cost is that a conversation opened this way is not
 * deep-linkable and Back does not close the overlay — Escape and the ✕ do, which is what the handoff
 * specifies for desktop anyway ("first Escape closes the conversation, second closes the overlay"). The
 * selection SURVIVES a close, so reopening the envelope returns you to what you were reading.
 */
export function InboxButton({ route, onNavigate, showHref }: InboxButtonProps) {
  const badge = useInboxBadge();
  // `lg` matches the shell swap (`ShellLayout` mounts `MobileShell` below `lg`), so the overlay-vs-navigate
  // decision here agrees with the layout `InboxScreen` renders (ART-153 pass two).
  const isDesktop = !usePhoneLayout();
  const [open, setOpen] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | undefined>(undefined);
  /**
   * SOMEBODY ELSE ASKED FOR A CONVERSATION (C-Entry) — a "Message" action on a crew profile, a friend's
   * card, a chat's member list. The store carries a PERSON and this opens the overlay on them; which
   * conversation that resolves to is `InboxScreen`'s question, already answered once there.
   *
   * The request is CONSUMED as it fires, which is what keeps it a request rather than a second source of
   * truth for "is the inbox open" — otherwise closing the overlay and reopening it would jump back to
   * whoever was last asked for.
   */
  const pendingPersonId = useMessagePanel((s) => s.personId);
  const consumeRequest = useMessagePanel((s) => s.consume);
  const [openWithPersonId, setOpenWithPersonId] = useState<string | undefined>(undefined);
  useEffect(() => {
    // DESKTOP ONLY. On a narrow viewport the inbox is a page, and the layout's `MessageRequestBridge` turns the
    // request into a navigation that carries the PERSON (`?with=`) — consuming it here as well would race that,
    // and the old narrow branch here navigated to the list and dropped who was asked for (ART-264).
    if (!pendingPersonId || !isDesktop) return;
    consumeRequest();
    setOpenWithPersonId(pendingPersonId);
    setSelectedThreadId(undefined);
    setOpen(true);
  }, [pendingPersonId, consumeRequest, isDesktop]);

  const count = badge.data ?? 0;
  // The VIEWPORT decides, and nothing else does. NotificationBell carries an `allowPanel` escape hatch
  // because the MOBILE HEADER mounts that same bell and has to refuse the popover; nothing mounts this
  // button on mobile chrome at all — the mobile shell reaches the inbox through its tab bar — so a second
  // knob here would be a branch with no caller, and the shells were passing it the identical
  // `usePhoneLayout()` this already reads.
  const useOverlay = isDesktop;

  return (
    <span className={styles.wrap}>
      <IconButton
        icon={MessageCircle}
        // The count lives in the BUTTON's accessible name. A second aria-label on the badge SPAN is
        // announced to nobody, because a control that already names itself does not fold in a child's —
        // the WCAG 4.1.2 defect NotificationBell's extraction found in these same two shells.
        label={count > 0 ? `Inbox, ${count} unread messages` : "Inbox"}
        tone="outline"
        iconSize={16}
        aria-haspopup={useOverlay ? "dialog" : undefined}
        aria-expanded={useOverlay ? open : undefined}
        onClick={() => (useOverlay ? setOpen(true) : onNavigate?.(route))}
      />
      {count > 0 && (
        <span className={styles.badge} aria-hidden="true">
          {count > 99 ? "99+" : count}
        </span>
      )}

      {useOverlay &&
        open &&
        /* PORTALLED TO document.body, which is the handoff's own wording and turns out to be load-bearing
           rather than stylistic. `ShellTopbar` is frosted — `backdrop-filter: blur(18px) saturate(180%)` —
           and an element with a backdrop-filter becomes the CONTAINING BLOCK for every `position: fixed`
           descendant. Rendered in place the overlay's `inset: 0` therefore resolved against the TOPBAR:
           measured in the running app at 1440×54, with the card 6px tall inside it. Nothing was clipped and
           nothing errored — the backdrop was fixed, its computed inset was 0, and it was simply zero-height,
           which is the same family as the stacking-context trap this codebase already records (a child
           overlay at a lower z-index landing on top because its parent made a context).

           The lesson generalises past the topbar: ANY overlay mounted inside chrome has to escape it, and
           `transform`, `filter`, `backdrop-filter`, `perspective`, `will-change` and `contain` all create
           that containing block silently. `Picker` portals its listbox for the same reason. */
        createPortal(
          <InboxScreen
            selectedThreadId={selectedThreadId}
            onSelectThread={setSelectedThreadId}
            // `onBack` is the mobile gesture, and this only renders on a desktop viewport — but a window
            // dragged narrow while the overlay is open flips InboxScreen into its one-pane layout, and
            // without this the back arrow there would do nothing.
            onBack={() => setSelectedThreadId(undefined)}
            // C-Esc's FIRST layer. The selection is this component's own state, so deselecting is a
            // setState — which is also why the prop exists at all: the routed page keeps the same selection
            // in the URL, and only the owner knows how to drop it.
            onCloseThread={() => setSelectedThreadId(undefined)}
            // Handed over ONCE and cleared as the inbox takes it, so reopening the envelope later returns
            // you to what you were reading rather than re-opening a conversation you asked for minutes ago.
            openWithPersonId={openWithPersonId}
            onOpenedWithPerson={() => setOpenWithPersonId(undefined)}
            showHref={showHref}
            onNavigate={onNavigate}
            onClose={() => setOpen(false)}
          />,
          document.body,
        )}
    </span>
  );
}
